import { auth } from "../firebase/firebaseConfig";
import apiClient from "../utils/ApiClient";

/**
 * Authorize.Net API surface.
 *
 * ROUTE PREFIX IS THE ENVIRONMENT SWITCH.
 *
 * Unlike the rest of the app -- which hits one set of paths and distinguishes
 * staging from production via the `x-collection-path` header -- the payment
 * routes are environment-specific by PATH. The backend binds the gateway
 * (sandbox vs live) to the route at registration time, precisely so a header
 * cannot select a payment gateway.
 *
 * `/dev/...` reaches the Authorize.Net SANDBOX. The production paths are not
 * registered on the backend yet; that is deliberate until staging sign-off.
 */

const isProductionGateway = process.env.REACT_APP_AUTHNET_ENV === "production";

// Keep the prefix in one place so there is a single thing to flip at go-live.
const PREFIX = isProductionGateway ? "" : "/dev";

export const AUTHNET_ENVIRONMENT = isProductionGateway ? "production" : "sandbox";

/**
 * Accept UI library. Sandbox and production are different HOSTS -- loading the
 * wrong one produces nonces the other environment rejects, which surfaces as a
 * confusing generic decline.
 */
export const ACCEPT_UI_SRC = isProductionGateway
  ? "https://js.authorize.net/v3/AcceptUI.js"
  : "https://jstest.authorize.net/v3/AcceptUI.js";

export const ACCEPT_CREDENTIALS = {
  apiLoginID: process.env.REACT_APP_AUTHNET_API_LOGIN_ID,
  clientKey: process.env.REACT_APP_AUTHNET_PUBLIC_CLIENT_KEY,
};

/**
 * Saved cards, off by default.
 *
 * Authorize.Net cannot charge a stored payment profile with a card code: PCI
 * forbids vaulting CVV, so no card code exists to send. With the account's
 * "Card Code = Required" setting on -- which is the only thing that makes CVV
 * enforceable at all, and the only thing that activates the Enhanced CCV filter
 * -- every saved-card charge is rejected with error 33 "Card Code is required."
 *
 * Collecting the CVV ourselves would solve it but move us out of PCI SAQ A: the
 * code is cardholder data, and taking it in our own input puts our servers in
 * scope. Accept UI has no CVV-only hosted field.
 *
 * So the choice is CVV enforcement OR saved cards, and we take enforcement.
 * The vaulting code stays in place and unused; flip this on to restore it.
 */
export const SAVED_CARDS_ENABLED =
  process.env.REACT_APP_SAVED_CARDS_ENABLED === "true";

/** True when the build has the keys Accept UI needs to render at all. */
export function hasAcceptCredentials() {
  return Boolean(ACCEPT_CREDENTIALS.apiLoginID && ACCEPT_CREDENTIALS.clientKey);
}

/**
 * Return a user object carrying a FRESH Firebase ID token.
 *
 * ApiClient builds its Authorization header from `user.accessToken`, which is
 * whatever Firebase last cached. UserContext only updates it via
 * onIdTokenChanged, and nothing in this app ever calls getIdToken() -- so once
 * the cached token passes its ~1 hour expiry, every authenticated request 401s
 * until the page is reloaded and Firebase mints a new one.
 *
 * That produced a saved-card list that worked right after a purchase, silently
 * emptied on a later visit, and came back on refresh. On the charge endpoint the
 * same staleness would fail the payment outright, which is why this wraps every
 * call here rather than just the read.
 *
 * getIdToken() returns the cached token when it is still valid and refreshes it
 * when it is not, so this is cheap on the common path.
 */
async function withFreshToken(user) {
  // Read the CURRENT user off the live Firebase instance rather than trusting
  // the context object. UserContext builds `user` with
  //   { uid, ...firestoreUserData, ...authUser }
  // and spreading a Firebase User copies only own enumerable properties --
  // accessToken is a prototype getter, so what survives that spread is not
  // dependable. Taking it from auth.currentUser removes the guesswork.
  const current = auth.currentUser;

  if (!current) {
    console.warn(
      "authorizeNet: no signed-in Firebase user; sending request without a refreshed token",
    );
    return user;
  }

  try {
    const accessToken = await current.getIdToken();
    // Only .accessToken is read downstream, so a shallow copy is sufficient.
    return { ...user, accessToken };
  } catch (error) {
    console.warn("authorizeNet: could not refresh ID token", error);
    return user;
  }
}

/**
 * Force-refresh the token and retry once.
 *
 * A 401 from our API means the token we sent didn't verify. getIdToken() serves
 * a cached token while it believes it is valid, so a token the SERVER rejects
 * can still be handed back unchanged -- the client and server can disagree
 * about validity (clock skew, revocation, a project mismatch). getIdToken(true)
 * bypasses the cache and asks Firebase for a genuinely new one.
 */
async function retryWithForcedToken(user, attempt) {
  const current = auth.currentUser;
  if (!current) return null;
  try {
    const accessToken = await current.getIdToken(true);
    console.warn("authorizeNet: got 401, retrying with a force-refreshed token");
    return await attempt({ ...user, accessToken });
  } catch (error) {
    console.warn("authorizeNet: forced token refresh failed", error);
    return null;
  }
}

async function readJson(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (error) {
    return { error: "Unexpected response from the server." };
  }
}

/**
 * Charge a cart.
 *
 * Resolves with { ok, status, data } rather than throwing on a decline: a
 * declined card is an expected outcome the checkout screen renders inline, not
 * an exception.
 *
 * Callers MUST distinguish three success-ish states:
 *   200 + success            -> paid, order created
 *   202 + held === true      -> held for fraud review; do NOT show a receipt
 *   402 + declined === true  -> declined; let the customer retry
 */
export async function payWithAuthorizeNet(payload, user) {
  const send = async (u) => {
    const response = await apiClient.post(`${PREFIX}/payment/authorizenet`, payload, u);
    const data = await readJson(response);
    return { ok: response.ok, status: response.status, data };
  };

  const authed = await withFreshToken(user);
  const result = await send(authed);

  // A 401 here means the token didn't verify server-side. Retrying with a
  // force-refreshed token turns a dead end into a recoverable blip -- and it is
  // safe to retry because no charge was attempted: the request never got past
  // authentication.
  if (result.status === 401) {
    const retried = await retryWithForcedToken(user, send);
    if (retried) return retried;
  }

  return result;
}

/**
 * Saved cards for the signed-in user. Returns [] when none are vaulted.
 *
 * A failed request also yields [], because an empty card list is a safe render
 * -- but it is logged rather than swallowed. Silently treating a 401 as "no
 * saved cards" is how a token-timing race turned into a card that only appeared
 * after a page refresh; callers must gate on auth being ready.
 */
export async function listSavedCards(user) {
  const send = async (u) =>
    apiClient.get(`${PREFIX}/authorizenet/payment-methods`, u);

  const authed = await withFreshToken(user);
  let response = await send(authed);

  // Retry a 401 with a forced refresh. Without this, a token the server
  // rejects renders as "you have no saved cards" -- which is why the section
  // appeared to come and go.
  if (response.status === 401) {
    const retried = await retryWithForcedToken(user, send);
    if (retried) response = retried;
  }

  if (!response.ok) {
    console.warn(
      `listSavedCards: ${response.status} ${response.statusText} — treating as no saved cards`,
    );
    return [];
  }
  const data = await readJson(response);
  return Array.isArray(data.paymentMethods) ? data.paymentMethods : [];
}

/** Remove a saved card. */
export async function deleteSavedCard(paymentProfileId, user) {
  const authed = await withFreshToken(user);
  const response = await apiClient.delete(
    `${PREFIX}/authorizenet/payment-methods/${encodeURIComponent(paymentProfileId)}`,
    authed,
  );
  return response.ok;
}

/**
 * Change the billing address on a saved card.
 *
 * The gateway takes billTo from the vault when charging a stored card and
 * rejects any per-transaction override (E00093), so this is the only way to
 * correct the address the customer's bank verifies against.
 */
export async function updateSavedCardBilling(paymentProfileId, billTo, user) {
  const authed = await withFreshToken(user);
  const response = await apiClient.put(
    `${PREFIX}/authorizenet/payment-methods/${encodeURIComponent(paymentProfileId)}`,
    { billTo },
    authed,
  );
  const data = await readJson(response);
  return { ok: response.ok, error: data.error };
}
